import { useState, useEffect, useContext } from 'react';
import { Box, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCrud } from '../../../lib/hooks';
import {
    AppContext,
    SttButtonCancel,
    SttButtonDelete,
    SttButtonRecover,
    SttButtonSave,
    SttFacilityList,
    SttFormCheck,
    SttFormDate,
    SttFormPrice,
    SttFormSelect,
    SttFormText,
    SttFormTextarea,
    SttFormTime,
    SttSelectFixedInterval,
    SelectGroupField,
    SttSelectWeekdays,
    SttSelectInterval,
} from '../../../sporttia/all';
import { parsePrice, subsetObject } from '../../../lib/utils';
import { PERMISSIONS } from '../../../lib/permissions';

type Facility = {
    id: number;
    name: string;
};

type BonoChipType = 'COMPLETE' | 'SINGLE';

type MandatoryGroup = {
    id: number;
    name: string;
} | null;

type Bono = {
    id: number | 'create';
    name: string;
    description?: string;
    price?: number;
    amount?: number;
    type: BonoChipType;
    duration: number;
    dateini: string;
    dateend: string;
    timeini: string;
    timeend: string;
    weekdays: number[];
    expiration: string | null;
    transferable: boolean;
    purchaseOnline: boolean;
    purchasePFs: string[];
    mandatoryGroup: MandatoryGroup;
    facilities?: Facility[];
    trash?: boolean;
};

type BonoTypeOption = {
    label: string;
    id: string;
};

type FormChangeEvent = {
    name: string | undefined;
    value: unknown;
};

type Props = {
    item: Bono | null | undefined;
    onSave?: (bono: Bono) => void;
    onDelete?: (bono: Bono) => void;
};

export default function BonoDetails({
    item,
    onSave,
    onDelete,
}: Props): React.ReactElement | null {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [bono, setBono, setProperty, , Post, Put, Delete] = useCrud<Bono>(
        item ?? undefined,
    );
    const [tpvEnabled, setTpvEnabled] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        setTpvEnabled(item?.purchasePFs?.includes('TPV') ?? false);
    }, [item]);

    function saveBono(): void {
        if (!bono) return;

        if (bono.id === 'create' && !bono.name?.trim()) {
            cxt.showMessage('E', t('NameRequired'));
            return;
        }

        setLoading(true);
        const params: Record<string, unknown> = subsetObject(
            bono,
            'name,description,price,amount,type,duration,dateini,dateend,timeini,timeend,weekdays,expiration,transferable',
        );
        params.price = parsePrice(params.price as string);
        params.paymentForms = tpvEnabled ? ['PURSE', 'TPV'] : ['PURSE'];
        params.onlinePurchase = bono.purchaseOnline;

        if (
            bono.mandatoryGroup &&
            bono.mandatoryGroup.name !== 'Ningún grupo'
        ) {
            params.idMandatoryGroup = bono.mandatoryGroup.id;
        } else {
            params.idMandatoryGroup = null;
        }

        if (params.expiration === '0000-00-00 00:00:00') {
            params.expiration = null;
        }

        if (bono.id !== 'create') {
            Put(`/bonos/${bono.id}`, params)
                .then((response) => {
                    const { bono: savedBono } = response as { bono: Bono };
                    setBono(savedBono);
                    onSave?.(savedBono);
                })
                .finally(() => setLoading(false));
        } else {
            Post(`/scs/${cxt.sc?.id}/bonos`, params)
                .then((response) => {
                    const { bono: savedBono } = response as { bono: Bono };
                    setBono(savedBono);
                    onSave?.(savedBono);
                })
                .finally(() => setLoading(false));
        }
    }

    function deleteBono(): void {
        Delete(`/bonos/${bono?.id}`).then((response) => {
            const { bono: deletedBono } = response as { bono: Bono };
            onDelete?.(deletedBono);
        });
    }

    function recoverBono(): void {
        if (!bono) return;

        Put(`/bonos/${bono.id}/recover`, {}, true).then(() => {
            const recoveredBono: Bono = {
                ...bono,
                trash: false,
            };
            setBono(recoveredBono);
            onSave?.(recoveredBono);
        });
    }

    function addFacility(facility: Facility): void {
        if (!bono) return;

        Post(`/bonos/${bono.id}/facilities/${facility.id}`).then(() => {
            setBono({
                ...bono,
                facilities: [...(bono.facilities || []), facility],
            });
        });
    }

    function deleteFacility(facility: Facility): void {
        if (!bono) return;

        Delete(`/bonos/${bono.id}/facilities/${facility.id}`).then(() => {
            setBono({
                ...bono,
                facilities: (bono.facilities || []).filter(
                    (fct) => fct.id !== facility.id,
                ),
            });
        });
    }

    const handleFormChange = ({ name, value }: FormChangeEvent): void => {
        if (!name) return;
        setProperty({ name, value });
    };

    if (!bono) {
        return null;
    }

    return (
        <>
            <Grid container spacing={3}>
                <Grid size={{ xs: 12, lg: 6 }}>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12 }}>
                            <SttFormText
                                caption={t('Name')}
                                name="name"
                                defVal={bono.name}
                                onChange={handleFormChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <SttFormTextarea
                                caption={t('Description')}
                                name="description"
                                defVal={bono.description}
                                onChange={handleFormChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <SttFormPrice
                                caption={t('Price')}
                                name="price"
                                defVal={bono.price?.toString() ?? ''}
                                onChange={handleFormChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <SttFormText
                                caption={t('Amount')}
                                name="amount"
                                defVal={bono.amount?.toString() ?? ''}
                                onChange={handleFormChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <SttFormSelect
                                caption={t('Type')}
                                name="type"
                                defVal={bono.type}
                                options={cxt.constants?.bonoTypes?.map(
                                    (item: BonoTypeOption) => ({
                                        caption: item.label,
                                        value: item.id,
                                    }),
                                )}
                                onChange={handleFormChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <SttSelectInterval
                                caption={t('Duration')}
                                name="duration"
                                labelWidth={90}
                                defVal={bono.duration}
                                min={5}
                                max={1435}
                                interval={5}
                                onChange={(value: number) =>
                                    setProperty({ name: 'duration', value })
                                }
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <SttFormDate
                                caption={t('ValidFrom')}
                                name="dateini"
                                defVal={bono.dateini}
                                onChange={handleFormChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <SttFormDate
                                caption={t('Until')}
                                name="dateend"
                                defVal={bono.dateend}
                                onChange={handleFormChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <SttFormTime
                                interval={30}
                                caption={t('TimeIni')}
                                name="timeini"
                                defVal={bono.timeini}
                                onChange={handleFormChange}
                                min="00:00:00"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <SttFormTime
                                interval={30}
                                caption={t('TimeEnd')}
                                name="timeend"
                                defVal={bono.timeend}
                                onChange={handleFormChange}
                                min="00:00:00"
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <SttSelectWeekdays
                                name="weekdays"
                                selected={bono.weekdays || []}
                                onChange={handleFormChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <SttSelectFixedInterval
                                caption={t('Expiration')}
                                name="expiration"
                                defVal={bono.expiration || ''}
                                options={['!FORTNIGHT']}
                                withMultiYear
                                onChange={handleFormChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <SelectGroupField
                                caption={t('MandatoryGroup')}
                                name="mandatoryGroup"
                                defVal={bono.mandatoryGroup}
                                onChange={handleFormChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <div>
                                <SttFormCheck
                                    caption={t('page.sc.bono.onlinePurchase')}
                                    name="purchaseOnline"
                                    checked={bono.purchaseOnline || false}
                                    onChange={handleFormChange}
                                />
                            </div>
                            <div>
                                <SttFormCheck
                                    caption={t('page.sc.bono.tpvPurchase')}
                                    checked={tpvEnabled}
                                    onChange={({ value }) =>
                                        setTpvEnabled(value as boolean)
                                    }
                                />
                            </div>
                            <div>
                                <SttFormCheck
                                    caption={t('page.sc.bono.transferable')}
                                    name="transferable"
                                    checked={bono.transferable || false}
                                    onChange={handleFormChange}
                                />
                            </div>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }}>
                    {bono.facilities && (
                        <SttFacilityList
                            permission={PERMISSIONS.VOUCHER.MODIFY}
                            status="ACTIVE,ONLY_SC"
                            facilities={bono.facilities}
                            onAdd={addFacility}
                            onDelete={deleteFacility}
                        />
                    )}
                </Grid>
            </Grid>

            <Box className="mt-6 flex justify-start gap-4 flex-wrap">
                {bono.id !== 'create' && !bono.trash && (
                    <SttButtonDelete
                        onClick={deleteBono}
                        permission={PERMISSIONS.VOUCHER.DELETE}
                    />
                )}

                {!bono.trash && (
                    <SttButtonSave
                        loading={loading}
                        onClick={saveBono}
                        permission={
                            bono.id === 'create'
                                ? PERMISSIONS.VOUCHER.CREATE
                                : PERMISSIONS.VOUCHER.MODIFY
                        }
                    />
                )}

                {bono.id === 'create' && (
                    <SttButtonCancel onClick={() => navigate(-1)} />
                )}

                {bono.trash && (
                    <SttButtonRecover
                        onClick={recoverBono}
                        permission={PERMISSIONS.VOUCHER.DELETE}
                    />
                )}
            </Box>
        </>
    );
}
